import { useState } from "react";
import { useStore } from "store";
import { useNavigate } from "react-router-dom";
import { useWindowDimensions } from "resources/BrowserManager";
import {
  Flex,
  Space,
  Card,
  Typography,
  Tag,
  Button,
  theme,
  message,
  Modal,
} from "antd";
const { Title, Text } = Typography;
import ApiManager from "resources/ApiManager/api";
import CONSTANTS from "shared/constants";
import strings from "resources/strings";

import {
  UserOutlined,
  Loading3QuartersOutlined,
  CrownFilled,
  DownloadOutlined,
} from "@ant-design/icons";

import "./style.scss";
const styleWrapperClass = "profile";

import ModalRequestCfdi from "./ModalRequestCfdi";

import { getFormattedUnixDate } from "shared/utilities";

export const TextLine = ({ label, values, wrap }) => {
  const { isSmallView } = useWindowDimensions();

  return (
    <Flex
      align="flex-start"
      gap={wrap && isSmallView ? 0 : 4}
      wrap={false}
      vertical={wrap && isSmallView}
    >
      {label && values?.some((i) => i) && (
        <Text
          strong
          style={{
            whiteSpace: "nowrap", // label stays in one line
            flexShrink: 0,
          }}
        >
          {label}:
        </Text>
      )}

      <Flex
        wrap="wrap"
        gap={3}
        style={{
          minWidth: 0,
          wordBreak: "normal",
          overflowWrap: "normal", // wrap only by words
        }}
      >
        {values?.map((i, index) => i && <Text key={index}>{i}</Text>)}
      </Flex>
    </Flex>
  );
};

export default function Perfil() {
  const { token: theme_token } = theme.useToken();
  const { isSmallView } = useWindowDimensions();
  const navigate = useNavigate();

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);

  const [isPdfLoading, setIsPdfLoading] = useState(false);

  const active_rfc_obj = ["nuevo-rfc", "onboarding"].includes(activeRfc)
    ? null
    : user.rfcs[activeRfc];

  const csf = active_rfc_obj.sat_csf?.data;
  const csf_last_update_unix = active_rfc_obj.sat_csf?.last_updated;

  const handleCsfPdfOpen = async () => {
    setIsPdfLoading(true);
    const apiRes = await ApiManager.getUserRfcCsfPdf(active_rfc_obj["rfc"]);
    setIsPdfLoading(false);

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === 3000) {
      message.error(strings.alertUnexpectedError);
      return apiRes.response.code;
    } else if (apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      return -1;
    }

    // apiRes is base64 string
    const byteCharacters = atob(apiRes.data.csf_bytes_b64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: "application/pdf",
    });

    //Download file
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "CSF_" + active_rfc_obj["rfc"] + ".pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const BannerFeatureNotAvailable = () => (
    <Space
      style={{
        marginTop: "20px",
        marginBottom: "20px",
        width: "100%",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Space direction="vertical">
        <Title level={3}>
          Disponible en el plan <CrownFilled className="fg-gold" /> Pro ó Básico
        </Title>
        <Title level={5}>
          Para ver el estado de tu perfil fiscal, deberás subscribirte al plan{" "}
          <CrownFilled className="fg-gold" /> Pro ó Básico.
        </Title>
        <Button onClick={() => navigate("/plan")}>Ver Planes</Button>
      </Space>
    </Space>
  );

  const CardRequestCfdi = () => (
    <Card>
      <Flex
        justify={isSmallView ? null : "space-between"}
        align={isSmallView ? null : "center"}
        vertical={isSmallView}
        gap={isSmallView ? 10 : 30}
      >
        <Flex vertical>
          <Text strong style={{ fontSize: "18px" }}>
            Solicita un CFDI
          </Text>
          <Text>
            Solicita un CFDI enviando solo los datos fiscales necesarios, sin
            compartir información confidencial.
          </Text>
        </Flex>
        <Flex vertical>
          <ModalRequestCfdi showIcon />
        </Flex>
      </Flex>
    </Card>
  );

  const CardDownloadCSF = () => (
    <Card>
      <Flex
        justify={isSmallView ? null : "space-between"}
        align={isSmallView ? null : "center"}
        vertical={isSmallView}
        gap={isSmallView ? 10 : 30}
      >
        <Flex vertical>
          <Text strong style={{ fontSize: "18px" }}>
            Descarga tu Constancia de Situación Fiscal
          </Text>
          <Text>
            Descarga tu CSF en formato PDF. Recuerda que este documento es
            confidencial y no es necesario compartirlo para solicitar facturas
            electrónicas (CFDI). Utiliza la función de{" "}
            <ModalRequestCfdi useTextButton /> para compartir solo los datos
            necesarios para recibir una factura electrónica (CFDI).
          </Text>
        </Flex>
        <Flex vertical>
          <Button
            type="primary"
            onClick={handleCsfPdfOpen}
            icon={<DownloadOutlined />}
            disabled={csf == null}
            loading={isPdfLoading}
          >
            Descargar CSF
          </Button>
        </Flex>
      </Flex>
    </Card>
  );

  const CardIdentity = () => (
    <Card>
      <Flex align="start" gap={8}>
        <UserOutlined style={{ fontSize: "20px" }} />

        <Flex vertical gap={isSmallView ? 5 : 0}>
          <TextLine
            label="Nombre Fiscal"
            values={[csf?.identificacion.nombre_fiscal]}
            wrap
          />
          <TextLine label="RFC" values={[csf?.identificacion.rfc]} />
          <TextLine label="CURP" values={[csf?.identificacion.curp]} />
          <TextLine
            label="Régimen Capital"
            values={[csf?.identificacion.regimen_capital]}
            wrap
          />
          <TextLine
            label="Nombre Comercial"
            values={[csf?.identificacion.nombre_comercial]}
            wrap
          />
          <TextLine
            label="Código Postal Fiscal"
            values={[csf?.domicilio.codigo_postal]}
          />
          <TextLine
            label="Inicio de operaciones"
            values={[csf?.identificacion.fecha_inicio_de_operaciones]}
            wrap
          />
          <TextLine
            label="Último cambio de estado"
            values={[csf?.identificacion.fecha_de_ultimo_cambio_de_estado]}
            wrap
          />
          <TextLine
            label="Estatus"
            values={[
              csf?.identificacion.estatus_en_el_padron && (
                <Tag
                  color={
                    csf?.identificacion.estatus_en_el_padron?.toLowerCase() ===
                    "activo"
                      ? "green"
                      : "red"
                  }
                >
                  {csf?.identificacion.estatus_en_el_padron?.toLowerCase() ===
                  "activo"
                    ? "Activo"
                    : "Inactivo"}
                </Tag>
              ),
            ]}
          />
          <TextLine
            label="Domicilio fiscal"
            values={[
              csf?.domicilio.tipo_de_vialidad,
              csf?.domicilio.nombre_de_vialidad,
              csf?.domicilio.numero_interior
                ? "INT. " + csf?.domicilio.numero_interior
                : null,
              csf?.domicilio.numero_exterior
                ? "EXT. " + csf?.domicilio.numero_exterior
                : null,
              csf?.domicilio.entre_calle
                ? csf?.domicilio.entre_calle !== "NA"
                  ? "ENTRE " +
                    csf?.domicilio.entre_calle +
                    " Y " +
                    csf?.domicilio.y_calle +
                    ","
                  : null
                : null,
              csf?.domicilio.nombre_de_la_colonia
                ? "COL. " + csf?.domicilio.nombre_de_la_colonia + ","
                : null,
              csf?.domicilio.nombre_de_la_localidad
                ? csf?.domicilio.nombre_de_la_localidad !==
                  csf?.domicilio.nombre_del_municipio_o_demarcacion_territorial
                  ? csf?.domicilio.nombre_de_la_localidad + ","
                  : null
                : null,
              csf?.domicilio.nombre_del_municipio_o_demarcacion_territorial
                ? csf?.domicilio
                    .nombre_del_municipio_o_demarcacion_territorial + ","
                : null,
              csf?.domicilio.nombre_de_la_entidad_federativa,
              csf?.domicilio.codigo_postal
                ? "C.P. " + csf?.domicilio.codigo_postal
                : null,
            ]}
            wrap
          />
        </Flex>
      </Flex>
    </Card>
  );

  const CardRegimenes = () => (
    <Card>
      <Flex vertical gap={10}>
        <Text strong style={{ fontSize: "18px" }}>
          Regímenes Fiscales
        </Text>
        {csf?.regimenes?.map((i, index) => (
          <Card
            key={index}
            styles={{ body: { padding: 15 } }}
            style={{
              border: "none",
              background: theme_token.colorLightGray,
            }}
          >
            <Flex
              align={isSmallView ? "start" : "center"}
              gap={isSmallView ? 3 : 8}
              vertical={isSmallView}
            >
              <Text strong style={{ fontSize: "16px" }}>
                {i.regimen}
              </Text>
              {i.fecha_fin ? (
                <Tag color="red">Inactivo</Tag>
              ) : (
                <Tag color="green">Activo</Tag>
              )}
            </Flex>
            <TextLine label="Fecha de inicio" values={[i.fecha_inicio]} />
            <TextLine label="Fecha de fin" values={[i.fecha_fin]} />
          </Card>
        ))}
      </Flex>
    </Card>
  );

  const CardActividades = () => (
    <Card>
      <Flex vertical gap={10}>
        <Text strong style={{ fontSize: "18px" }}>
          Actividades Económicas
        </Text>
        {csf?.actividades_economicas?.map((i, index) => (
          <Card
            key={index}
            styles={{ body: { padding: 15 } }}
            style={{
              border: "none",
              background: theme_token.colorLightGray,
            }}
          >
            <Flex
              align={isSmallView ? "start" : "center"}
              gap={isSmallView ? 3 : 8}
              vertical={isSmallView}
            >
              <Text strong style={{ fontSize: "16px" }}>
                {i.actividad}
              </Text>
              {i.fecha_fin ? (
                <Tag color="red">Inactiva</Tag>
              ) : (
                <Tag color="green">Activa</Tag>
              )}
            </Flex>
            <TextLine label="Fecha de inicio" values={[i.fecha_inicio]} />
            <TextLine label="Fecha de fin" values={[i.fecha_fin]} />
            <TextLine label="Porcentaje" values={[i.porcentaje + "%"]} />
          </Card>
        ))}
      </Flex>
    </Card>
  );

  const dateContainer = isSmallView ? (
    <Space direction="vertical" size={0}>
      <Text italic>Última actualización:</Text>
      {getFormattedUnixDate(csf_last_update_unix)}
    </Space>
  ) : (
    <Space>
      <Text italic>Última actualización:</Text>
      {getFormattedUnixDate(csf_last_update_unix)}
    </Space>
  );

  return (
    <div className={styleWrapperClass}>
      <Title level={1}>Perfil Fiscal</Title>

      {["pro", "basic"].includes(active_rfc_obj.plan_name) ? (
        <>
          <Title level={4}>Monitorea el estado de tu perfil fiscal.</Title>
          {csf ? (
            <Flex
              style={{ marginTop: "30px", marginBottom: "30px" }}
              vertical
              gap={30}
            >
              <CardRequestCfdi />
              <CardDownloadCSF />
              <CardIdentity />
              <CardRegimenes />
              <CardActividades />
              <Text>{dateContainer}</Text>
            </Flex>
          ) : (
            <Flex vertical align="center" style={{ marginTop: "50px" }}>
              <Text style={{ fontSize: "18px", textAlign: "center" }}>
                La información de tu perfil fiscal se está cargando. Te
                notificaremos cuando esté lista.
              </Text>
              <Loading3QuartersOutlined
                spin={true}
                style={{
                  fontSize: "20px",
                  color: "#801794",
                  margin: "20px 0px",
                }}
              />
            </Flex>
          )}
        </>
      ) : (
        <BannerFeatureNotAvailable />
      )}
    </div>
  );
}
